import { supabase } from "@/api/supabaseClient";

export type CareSiteOwnership = "private" | "organization";
export type CareSiteKind = "home" | "station" | "external_hibernate" | "other";
export type CareSiteAccessRole = "staff" | "manager";
export type PlaceNodeKind =
  | "site_root"
  | "building"
  | "room"
  | "shelf"
  | "cage"
  | "box"
  | "enclosure"
  | "area"
  | "other";
export type PlaceVisibility = "org_coarse" | "internal";

export type CareSiteRow = {
  id: string;
  organization_id: string;
  ownership: CareSiteOwnership;
  owner_profile_id: string | null;
  name: string;
  kind: CareSiteKind;
  capacity_hint: number | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

export type PlaceNodeRow = {
  id: string;
  care_site_id: string;
  parent_id: string | null;
  name: string;
  node_kind: PlaceNodeKind;
  is_occupiable: boolean;
  visibility: PlaceVisibility;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

export type CareSiteAccessRow = {
  id: string;
  care_site_id: string;
  profile_id: string;
  access_role: CareSiteAccessRole;
  granted_by: string | null;
  valid_from: string;
  valid_to: string | null;
  created_at: string;
  updated_at: string;
};

export type SiteAccessWindow = {
  profileId: string;
  validFrom: string | null;
  validTo: string | null;
};

/**
 * Dieselbe Grenze wie private_helpers.has_site_access
 * (supabase/migrations/20260720200000_p2_care_sites.sql, gültiges Fenster):
 * profile_id = auth.uid() AND valid_from <= asOf AND (valid_to IS NULL OR valid_to > asOf).
 * NULL als valid_from ist in SQL nicht wahr und schließt aus.
 * Vereinsrolle, Eigentum und can_manage_site zählen nicht: die Aufnahme-RPC
 * create_animal_case ruft nur has_site_access. public.has_site_access(uuid) ist
 * dieselbe Regel, aber nur für eine Stellen-Id, deshalb bleibt die Liste eine
 * Abfrage plus dieses Prädikat.
 */
export function siteAccessCovers(
  row: SiteAccessWindow,
  userId: string,
  asOf: Date,
): boolean {
  if (!userId || row.profileId !== userId) return false;
  if (row.validFrom == null) return false;
  const from = new Date(row.validFrom);
  if (Number.isNaN(from.getTime()) || from.getTime() > asOf.getTime()) return false;
  if (row.validTo == null) return true;
  const to = new Date(row.validTo);
  if (Number.isNaN(to.getTime())) return false;
  return to.getTime() > asOf.getTime();
}

function accessInstant(value: unknown): string | null | undefined {
  if (value == null) return null;
  if (typeof value === "string") return value;
  return undefined;
}

export class CareSitesApi {
  /**
   * Eigene, gerade gültige Zugriffszeilen. Das Fenster ist siteAccessCovers,
   * nicht can_manage_site: ein Vereinsadmin ohne Zeile kommt bei der Aufnahme
   * nicht hinein. RLS kann über can_manage_site fremde Zeilen zeigen; die
   * bleiben hier draußen, weil die Profil-Id nicht die eigene ist.
   */
  static async listMyWritableSiteIds(asOf: Date = new Date()): Promise<string[]> {
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();
    if (userError) throw userError;
    if (!user?.id) return [];
    const { data, error } = await supabase
      .from("care_site_access")
      .select("care_site_id, profile_id, valid_from, valid_to")
      .eq("profile_id", user.id);
    if (error) throw error;
    const ids = new Set<string>();
    for (const row of data ?? []) {
      if (typeof row.care_site_id !== "string" || row.care_site_id.length === 0) continue;
      const validFrom = accessInstant(row.valid_from);
      const validTo = accessInstant(row.valid_to);
      if (validFrom === undefined || validTo === undefined) continue;
      if (
        siteAccessCovers(
          {
            profileId: typeof row.profile_id === "string" ? row.profile_id : "",
            validFrom,
            validTo,
          },
          user.id,
          asOf,
        )
      ) {
        ids.add(row.care_site_id);
      }
    }
    return [...ids];
  }

  static async listMySites(): Promise<CareSiteRow[]> {
    const { data, error } = await supabase
      .from("care_sites")
      .select("*")
      .eq("is_active", true)
      .order("name", { ascending: true });
    if (error) throw error;
    return (data ?? []) as CareSiteRow[];
  }

  static async getSite(siteId: string): Promise<CareSiteRow | null> {
    const { data, error } = await supabase
      .from("care_sites")
      .select("*")
      .eq("id", siteId)
      .maybeSingle();
    if (error) throw error;
    return (data as CareSiteRow | null) ?? null;
  }

  static async createPrivateSite(input: {
    organizationId: string;
    name: string;
    kind?: CareSiteKind;
  }): Promise<CareSiteRow> {
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();
    if (userError) throw userError;
    if (!user?.id) throw new Error("Nicht angemeldet");

    const { data, error } = await supabase
      .from("care_sites")
      .insert({
        organization_id: input.organizationId,
        ownership: "private",
        owner_profile_id: user.id,
        name: input.name.trim(),
        kind: input.kind ?? "home",
      })
      .select("*")
      .single();
    if (error) throw error;
    return data as CareSiteRow;
  }

  static async listPlaceNodes(siteId: string): Promise<PlaceNodeRow[]> {
    const { data, error } = await supabase
      .from("place_nodes")
      .select("*")
      .eq("care_site_id", siteId)
      .eq("is_active", true)
      .order("sort_order", { ascending: true })
      .order("name", { ascending: true });
    if (error) throw error;
    return (data ?? []) as PlaceNodeRow[];
  }

  static async createPlaceNode(input: {
    careSiteId: string;
    parentId: string | null;
    name: string;
    nodeKind: PlaceNodeKind;
    visibility?: PlaceVisibility;
  }): Promise<PlaceNodeRow> {
    const isOccupiable = ["cage", "box", "enclosure", "other"].includes(
      input.nodeKind
    );
    const { data, error } = await supabase
      .from("place_nodes")
      .insert({
        care_site_id: input.careSiteId,
        parent_id: input.parentId,
        name: input.name.trim(),
        node_kind: input.nodeKind,
        is_occupiable: isOccupiable,
        visibility: input.visibility ?? "internal",
      })
      .select("*")
      .single();
    if (error) throw error;
    return data as PlaceNodeRow;
  }

  static async deactivatePlaceNode(nodeId: string): Promise<void> {
    const { error } = await supabase
      .from("place_nodes")
      .update({ is_active: false })
      .eq("id", nodeId);
    if (error) throw error;
  }

  static async listAccess(siteId: string): Promise<CareSiteAccessRow[]> {
    const { data, error } = await supabase
      .from("care_site_access")
      .select("*")
      .eq("care_site_id", siteId)
      .order("valid_from", { ascending: true });
    if (error) throw error;
    return (data ?? []) as CareSiteAccessRow[];
  }

  static async canManageSite(siteId: string): Promise<boolean> {
    const { data, error } = await supabase.rpc("can_manage_site", {
      p_care_site_id: siteId,
    });
    if (error) throw error;
    return !!data;
  }
}
